import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import type { SupportTicketAttachment } from '@bliks-io/contracts';
import {
  BliksLoader,
  Dialog,
  DialogShell,
  DialogShellBody,
  DialogShellFooter,
  DialogShellHeader
} from '@bliks-io/ui';
import { useSupportAttachmentImage } from '@/api/queries/useSupportTicket';
import { formatFileSize } from '@/lib/formatters';

interface AttachmentPreviewDialogProps {
  ticketId: number;
  messageId: number;
  attachment: SupportTicketAttachment | null;
  onDownload: (attachment: SupportTicketAttachment) => void;
  onClose: () => void;
}

function useObjectUrl(blob: Blob | undefined) {
  const url = useMemo(() => (blob ? URL.createObjectURL(blob) : undefined), [blob]);
  useEffect(
    function revokeReplacedUrl() {
      return () => {
        if (url) URL.revokeObjectURL(url);
      };
    },
    [url]
  );
  return url;
}

export function AttachmentPreviewDialog({
  ticketId,
  messageId,
  attachment,
  onDownload,
  onClose
}: AttachmentPreviewDialogProps) {
  const { t } = useTranslation();
  const { data, isError } = useSupportAttachmentImage(ticketId, messageId, attachment);
  const src = useObjectUrl(data);

  if (!attachment) return null;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogShell size="xl">
        <DialogShellHeader
          title={attachment.filename}
          subtitle={formatFileSize(attachment.sizeBytes)}
          onClose={onClose}
        />
        <DialogShellBody className="items-center justify-center">
          {isError && (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              {t('supportTickets.detail.attachmentError')}
            </p>
          )}
          {/* Each image is several Zammad round trips away (ownership, then the
              visible conversation, then the bytes): about half a second. */}
          {!isError && !src && (
            <div className="flex min-h-64 w-full items-center justify-center">
              <BliksLoader label={t('common.loading')} />
            </div>
          )}
          {/* An <img> never runs script, which is what makes rendering the
              customer-visible file on our origin acceptable at all. */}
          {src && (
            <img src={src} alt={attachment.filename} className="h-auto max-w-full object-contain" />
          )}
        </DialogShellBody>
        <DialogShellFooter
          secondary={{ label: t('common.close'), onClick: onClose }}
          primary={{
            label: t('supportTickets.detail.attachmentDownloadAction'),
            onClick: () => onDownload(attachment)
          }}
        />
      </DialogShell>
    </Dialog>
  );
}
